import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import useMediaQuery from '@mui/system/useMediaQuery';
import colors from '../../theme/colors';
import { breakpoints } from '../../theme';
import * as T from '../Typography';
import Button from '../Button';
import surveyOpenSvg from '../assets/survey-open.svg';
import surveyClosedSvg from '../assets/survey-closed.svg';
import surveyCompletedSvg from '../assets/survey-completed.svg';
import feedbackReportReadySvg from '../assets/feedback-report-ready.svg';
import { SurveyCardWrapper, IconWrapper, ButtonGroup } from './style';
import { formatDate } from '../../helpers/format-date';
import DownloadSurveyConfirmModal from '../DownloadSurveyConfirmModal';
import { surveyTypes } from '../../constants/data-types';
import { routes } from '../../constants';
import { handleFileDownload } from '../../helpers/handle-file-download';
import { useAuth } from '../../context/auth';
import { useGetUserSurvey } from '../../api-calls/survey.queries';
import { supportEmail } from '../../constants/external';

type SurveyCardProps = {
  status: 'closed' | 'open' | 'in_progress' | 'completed' | 'report_ready';
  startDate: string;
  endDate: string;
};

type Content = {
  title: string;
  description: JSX.Element;
  buttons: { label: string; action: () => void; isLoading?: boolean }[];
  imgSrc: string;
  cardBackground?: string;
} | null;

const SurveyCard: React.FC<SurveyCardProps> = ({
  status,
  startDate,
  endDate
}) => {
  const { user, token } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const { data } = useGetUserSurvey({
    salesforceContactId: user?.salesforceContactId || ''
  });
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const isWideScreen = useMediaQuery(`(min-width:${breakpoints.tablet}px)`);
  const { VITE_SF_SURVEY_URL, VITE_API_URL, VITE_SANDBOX_SUFFIX } = import.meta.env;
  const surveyDownloadUrl = `${VITE_SF_SURVEY_URL}/indexpdf${VITE_SANDBOX_SUFFIX ?? ''}?contactid=${user?.salesforceContactId}`;
  const reportDownloadUrl = `${VITE_API_URL}/survey/${user?.salesforceContactId}/download-report?type=feedback`;


  const navigateToFirstSection = () =>
    navigate(routes.SURVEY.replace(':sectionOrder', '-1'));
  const renderContent = (): Content => {
    switch (status) {
      case surveyTypes.CLOSED:
        return {
          title: 'The survey is currently closed.',
          description: (
            <>
              Check back on <strong>{formatDate(startDate)}</strong> when it
              opens again.
            </>
          ),
          buttons: [],
          imgSrc: surveyClosedSvg,
          cardBackground: colors.neutral.shades.shade100
        };
      case surveyTypes.SURVEY_ENDED:
        return {
          title: 'The survey window has closed.',
          description: (
            <>
              The survey window closed on <strong>{formatDate(endDate)}</strong>. Please reach out via email to <a href={`mailto:${supportEmail}`}>{supportEmail}</a> if you have any questions.
            </>
          ),
          buttons: [],
          imgSrc: surveyClosedSvg,
          cardBackground: colors.neutral.shades.shade100
        };
      case surveyTypes.OPEN:
        return {
          title: 'The survey is now open!',
          description: (
            <>
              The survey is now available. Please complete it at your
              convenience before it closes on{' '}
              <strong>{formatDate(endDate)}</strong>.
            </>
          ),
          buttons: [
            { label: 'Download', action: () => setShowModal(!showModal) },
            { label: 'Start survey', action: navigateToFirstSection }
          ],
          imgSrc: surveyOpenSvg,
          cardBackground: colors.neutral.shades.shade100
        };
      case surveyTypes.IN_PROGRESS:
        return {
          title: 'The survey is now open!',
          description: (
            <>
              The survey is now available. Please complete it at your
              convenience before it closes on{' '}
              <strong>{formatDate(endDate)}</strong>.
            </>
          ),
          buttons: [
            { label: 'Download', action: () => setShowModal(!showModal) },
            { label: 'Continue survey', action: navigateToFirstSection }
          ],
          imgSrc: surveyOpenSvg,
          cardBackground: colors.neutral.shades.shade100
        };
      case surveyTypes.COMPLETED:
        return {
          title: "You've completed the survey!",
          description: (
            <>
              Your responses have been successfully submitted. We'll be in touch when your feedback report is ready.
            </>
          ),
          buttons: [
            {
              label: 'Download',
              action: () => handleFileDownload(surveyDownloadUrl, 'survey.pdf', token, setIsDownloading),
              isLoading: isDownloading
            },
            {
              label: 'View survey', action: () =>
                window.open(
                  surveyDownloadUrl,
                  '_blank'
                )
            }
          ],
          imgSrc: surveyCompletedSvg,
          cardBackground: colors.neutral.shades.shade100
        };
      case surveyTypes.REPORT_READY:
        return {
          title: 'Your Feedback Report is ready!',
          description: (
            <>
              Access detailed insights and analysis. Dive in to explore the
              results and learn from the data.
            </>
          ),
          buttons: [
            {
              label: 'Download',
              action: () => handleFileDownload(reportDownloadUrl, '', token, setIsDownloading),
              isLoading: isDownloading
            },
            {

              label: 'View full Report', action: () => {
                const url = routes.VIEW_REPORT.replace(':salesforceContactId', user?.salesforceContactId || '');
                const urlWithToken = `${url}?token=${encodeURIComponent(token || '')}&type=feedback`;
                window.open(urlWithToken, '_blank');
              }
            }
          ],
          imgSrc: feedbackReportReadySvg,
          cardBackground: colors.secondary.shades.shade50
        };
      default:
        return null;
    }
  };

  const content = renderContent();

  if (!content) return null;

  return (
    <SurveyCardWrapper
      flexDirection={isWideScreen ? 'row' : 'column'}
      cardBackground={content.cardBackground}
      data-testid="survey-card"
    >
      <IconWrapper>
        <img src={content.imgSrc} alt="Survey Icon" />
      </IconWrapper>
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        mb={isWideScreen ? 0 : 3}
      >
        <T.TextMediumBold
          color={colors.neutral.shades.shade700}
          ta={isWideScreen ? 'left' : 'center'}
        >
          {content.title}
        </T.TextMediumBold>
        <T.TextSmallRegular
          mt={2}
          color={colors.neutral.shades.shade600}
          ta={isWideScreen ? 'left' : 'center'}
        >
          {content.description}
        </T.TextSmallRegular>
      </Box>
      <ButtonGroup
        flexDirection={isWideScreen ? 'row' : 'column'}
        alignItems={isWideScreen ? 'flex-end' : 'center'}
      >
        {content.buttons.map((button, index) => (
          <Button
            key={index}
            onClick={(e: any) => {
              e.preventDefault();
              e.stopPropagation();
              button.action();
            }}
            variant={index === 0 ? 'primary' : 'blue'}
            width={isWideScreen ? 'fit-content' : '100%'}
            loading={button.isLoading}
          >
            {button.label}
          </Button>
        ))}
      </ButtonGroup>
      <DownloadSurveyConfirmModal
        visible={showModal}
        setIsModalVisible={() => setShowModal(!showModal)}
      />
    </SurveyCardWrapper>
  );
};

export default SurveyCard;
